import { useEffect, useState } from "react";
import { PopupOptionsSetter, QuoteRejectReason } from "src/ts";
import { useSetPopup } from "../use-popup";
import { GlobalService } from "../../../hexagonal-architecture-frontend-base/src/domain/services/Global.service";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components";
import { useGetQuoteRejectReasons } from "../../quotes";

interface RejectionReasonSelectorProps {
    text: string;
    confirmLabel: string;
    cancelLabel: string;
    onConfirm: (reason: string) => void;
    onCancel: () => void;
}

const RejectionReasonSelector = ({
    text,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onCancel,
}: RejectionReasonSelectorProps) => {
    const { t } = useTranslation();
    const [selectedReason, setSelectedReason] = useState<string>("");
    const [reasons, setReasons] = useState<QuoteRejectReason[]>([]);
    const { getQuoteRejectReasons, isLoading } = useGetQuoteRejectReasons();

    useEffect(() => {
        getQuoteRejectReasons(undefined, {
            onSuccess: (data: QuoteRejectReason[]) => {
                setReasons(data.filter((r: QuoteRejectReason) => r.value !== "-None-"));
            },
        });
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <p>{text}</p>
            <label className="font-semibold text-m">
                {GlobalService.uppercaseFirstLetter(t("pages.informeDetallado.rejectionReason"))}:
            </label>
            <select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-gray-500"
            >
                <option value="" disabled>
                    {isLoading
                        ? t("pages.informeDetallado.loadingReasons")
                        : t("pages.informeDetallado.selectReason")}
                </option>
                {reasons.map((reason) => (
                    <option key={reason.id} value={reason.value}>
                        {reason.value}
                    </option>
                ))}
            </select>
            <div className="flex flex-col gap-2 mt-2">
                <Button
                    styleType="secondary"
                    fullWidth={true}
                    disabled={!selectedReason}
                    onClick={() => selectedReason && onConfirm(selectedReason)}
                >
                    {confirmLabel}
                </Button>
                <Button styleType="primary" fullWidth={true} onClick={onCancel}>
                    {cancelLabel}
                </Button>
            </div>
        </div>
    );
};

export const useShowConfirmRejectedQuotePopup = () => {
    const { t } = useTranslation();
    const { openPopup, closePopup } = useSetPopup();

    const showConfirmRejectedQuotePopup = (
        onConfirm: (reason: string) => void
    ) => {
        const confirmRejectedQuotePopupOptions: PopupOptionsSetter = {
            title: GlobalService.uppercaseAfterPointFirstsLetters(
                t("pages.informeDetallado.modalConfirmRejectQuote.title")
            ),
            children: (
                <RejectionReasonSelector
                    text={GlobalService.uppercaseAfterPointFirstsLetters(
                        t("pages.informeDetallado.modalConfirmRejectQuote.text")
                    )}
                    confirmLabel={GlobalService.uppercaseFirstLetter(
                        t(
                            "pages.informeDetallado.modalConfirmRejectQuote.confirmBtn"
                        )
                    )}
                    cancelLabel={GlobalService.uppercaseFirstLetter(
                        t(
                            "pages.informeDetallado.modalConfirmRejectQuote.cancelBtn"
                        )
                    )}
                    onConfirm={(reason) => {
                        onConfirm(reason);
                        closePopup();
                    }}
                    onCancel={() => closePopup()}
                />
            ),
        };

        openPopup(confirmRejectedQuotePopupOptions);
    };

    return { showConfirmRejectedQuotePopup };
};
